<template>
  <div class="container-fluid py-4">
    <div class="d-flex justify-content-between align-items-center mb-3 header-clinique flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-cash-coin fs-2 text-success"></i>
        <div>
          <h2 class="fw-bold mb-0">Encaissements</h2>
          <small class="text-muted">Registre unique : consultations, examens et services, hospitalisations, pharmacie, remboursements.</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button v-if="peutExporter" class="btn btn-outline-success" :disabled="export_.enCours" @click="exporterCsv">
          <i class="bi bi-file-earmark-spreadsheet me-1"></i>{{ export_.enCours ? 'Export…' : 'Exporter' }}
        </button>
        <button v-if="peutImprimer" class="btn btn-outline-primary" @click="imprimerListe"><i class="bi bi-printer me-1"></i>Imprimer</button>
      </div>
    </div>

    <!-- Filtres -->
    <div class="card card-clinique p-3 mb-3">
      <div class="row g-2 align-items-end">
        <div class="col-12 col-lg-3">
          <label class="form-label small fw-semibold mb-1">Recherche</label>
          <input v-model.trim="f.recherche" class="form-control" placeholder="N° de reçu, patient, n° de dossier, référence…" @input="relancerApres">
        </div>
        <div class="col-6 col-lg-2">
          <label class="form-label small fw-semibold mb-1">Du</label>
          <input v-model="f.du" type="date" class="form-control" @change="relancer">
        </div>
        <div class="col-6 col-lg-2">
          <label class="form-label small fw-semibold mb-1">Au</label>
          <input v-model="f.au" type="date" class="form-control" @change="relancer">
        </div>
        <div class="col-6 col-lg-2">
          <label class="form-label small fw-semibold mb-1">Nature</label>
          <select v-model="f.type" class="form-select" @change="relancer">
            <option value="">Toutes</option>
            <option v-for="(lib, code) in types" :key="code" :value="code">{{ lib }}</option>
          </select>
        </div>
        <div class="col-6 col-lg-1">
          <label class="form-label small fw-semibold mb-1">Statut</label>
          <select v-model="f.statut" class="form-select" @change="relancer">
            <option value="">Tous</option>
            <option value="VALIDE">Validés</option>
            <option value="ANNULE">Annulés</option>
            <option value="REMBOURSE">Remboursés</option>
          </select>
        </div>
        <div class="col-6 col-lg-2">
          <label class="form-label small fw-semibold mb-1">Moyen</label>
          <select v-model="f.moyen" class="form-select" @change="relancer">
            <option value="">Tous</option>
            <option v-for="m in moyens" :key="m.id" :value="m.id">{{ m.nom }}</option>
          </select>
        </div>
        <div v-if="assurances.length" class="col-6 col-lg-2">
          <label class="form-label small fw-semibold mb-1">Assurance</label>
          <select v-model="f.assurance" class="form-select" @change="relancer">
            <option value="">Toutes</option>
            <option v-for="a in assurances" :key="a.id" :value="a.id">{{ a.nom }}</option>
          </select>
        </div>
        <div class="col-12 col-lg-auto d-flex flex-wrap gap-1">
          <button v-for="p in periodes" :key="p.code" class="btn btn-sm" :class="periode === p.code ? 'btn-primary' : 'btn-outline-primary'" @click="choisirPeriode(p.code)">{{ p.libelle }}</button>
          <button class="btn btn-sm btn-link" @click="reinitialiser">Réinitialiser</button>
        </div>
      </div>
    </div>

    <!-- Totaux (tous les encaissements filtrés, annulés exclus) -->
    <div class="row g-3 mb-3">
      <div class="col-6 col-lg-3"><div class="card card-clinique p-3 h-100"><small class="text-muted">Reçu des patients</small><div class="fs-4 fw-bold text-success">{{ fcfa(totaux.encaisse) }}</div></div></div>
      <div class="col-6 col-lg-3"><div class="card card-clinique p-3 h-100"><small class="text-muted">Part des assurances (à recevoir)</small><div class="fs-4 fw-bold text-primary">{{ fcfa(totaux.assurance) }}</div></div></div>
      <div class="col-6 col-lg-3"><div class="card card-clinique p-3 h-100"><small class="text-muted">Montant des actes réglés</small><div class="fs-4 fw-bold">{{ fcfa(totaux.montant) }}</div></div></div>
      <div class="col-6 col-lg-3"><div class="card card-clinique p-3 h-100">
        <small class="text-muted">Par moyen de paiement</small>
        <div v-for="(v, m) in totaux.parMoyen" :key="m" class="d-flex justify-content-between small"><span>{{ m }}</span><strong>{{ fcfa(v) }}</strong></div>
        <div v-if="!Object.keys(totaux.parMoyen || {}).length" class="small text-muted">—</div>
      </div></div>
    </div>
    <div v-if="Object.keys(totaux.parType || {}).length" class="d-flex flex-wrap gap-2 mb-3">
      <button v-for="(t, code) in totaux.parType" :key="code" class="btn btn-sm btn-light border" @click="f.type = f.type === code ? '' : code; relancer()">
        {{ types[code] || code }} · <strong>{{ t.nombre }}</strong> · {{ fcfa(t.encaisse) }}
      </button>
    </div>

    <div class="card card-clinique p-0">
      <div v-if="chargement" class="text-center py-5"><div class="spinner-border text-primary"></div></div>
      <div v-else class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Reçu</th><th>Patient</th><th>Objet</th>
              <th class="text-end">Montant</th><th class="text-end">Assurance</th><th class="text-end">Patient</th>
              <th>Moyen</th><th>Statut</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in lignes" :key="p.id" :class="{ 'text-muted': p.statut !== 'VALIDE', 'table-warning': p.type === 'REMBOURSEMENT' }">
              <td class="text-nowrap"><span class="fw-semibold">{{ p.numero }}</span><div class="small text-muted">{{ dateHeure(p.date) }}</div></td>
              <td>
                <router-link v-if="p.patient" :to="`/dossiers/${p.patient.id}`">{{ p.patient.nom }}</router-link>
                <span v-else class="text-muted">—</span>
                <div v-if="p.patient?.numeroDossier" class="small text-muted">{{ p.patient.numeroDossier }}</div>
              </td>
              <td class="small">
                {{ types[p.type] || p.type }}
                <div class="text-muted">{{ p.acte?.numero || '' }}<span v-if="p.origine"> · rembourse {{ p.origine.numero }}</span></div>
              </td>
              <td class="text-end text-nowrap">{{ fcfa(p.montant) }}</td>
              <td class="text-end text-nowrap">
                <span v-if="p.partAssurance > 0">{{ fcfa(p.partAssurance) }}<div class="small text-muted">{{ p.assurance }}<span v-if="p.taux"> · {{ p.taux }} %</span></div></span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-end text-nowrap fw-semibold" :class="p.partPatient < 0 ? 'text-danger' : ''">{{ fcfa(p.partPatient) }}</td>
              <td class="small">{{ p.moyen || '—' }}<div class="text-muted">{{ p.caissier || '' }}</div></td>
              <td>
                <span class="badge" :class="{ VALIDE: 'bg-success', ANNULE: 'bg-secondary', REMBOURSE: 'bg-warning text-dark' }[p.statut]">{{ statuts[p.statut] || p.statut }}</span>
                <div v-if="p.motifAnnulation" class="small text-muted" :title="p.motifAnnulation">{{ tronquer(p.motifAnnulation) }}<span v-if="p.annulePar"> · {{ p.annulePar }}</span></div>
              </td>
              <td class="text-end text-nowrap">
                <button class="btn btn-sm btn-outline-secondary" title="Imprimer le reçu" @click="imprimerRecu(p)"><i class="bi bi-receipt"></i></button>
                <button v-if="peutCorriger(p)" class="btn btn-sm ms-1" :class="p.correction === 'REMBOURSEMENT' ? 'btn-outline-warning' : 'btn-outline-danger'"
                        :title="p.correction === 'REMBOURSEMENT' ? 'Caisse clôturée : rembourser' : 'Annuler'" @click="corriger(p)">
                  <i :class="p.correction === 'REMBOURSEMENT' ? 'bi bi-arrow-counterclockwise' : 'bi bi-x-circle'"></i>
                </button>
                <i v-else-if="p.correctionImpossible" class="bi bi-lock text-muted ms-2" :title="p.correctionImpossible"></i>
              </td>
            </tr>
 <tr v-if="!lignes.length"><td colspan="9" class="text-center text-muted py-4">Aucun encaissement pour ces critères.</td></tr>
          </tbody>
        </table>
      </div>
      <div class="px-3 pb-3">
        <Pagination v-model:page="page" v-model:par-page="parPage" :total="total" @update:page="charger" @update:par-page="charger" />
      </div>
    </div>
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'
import Pagination from '@/components/commun/Pagination.vue'
import { demanderMotif } from '@/components/commun/dialogue'
import { imprimer, fcfa as fcfaImpression, e, dateFr } from '@/impression/impression'
import { documentRecu } from '@/impression/documents/recu'

const aujourdhui = () => new Date().toISOString().slice(0, 10)
const ilYa = (jours) => { const d = new Date(); d.setDate(d.getDate() - jours); return d.toISOString().slice(0, 10) }
const debutMois = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1, 12).toISOString().slice(0, 10) }

export default {
  name: 'ListePaiement',
  components: { Pagination },
  data() {
    return {
      f: { recherche: '', du: aujourdhui(), au: aujourdhui(), type: '', statut: '', moyen: '', assurance: '' },
      periode: 'jour',
      periodes: [
        { code: 'jour', libelle: "Aujourd'hui" },
        { code: 'semaine', libelle: '7 jours' },
        { code: 'mois', libelle: 'Ce mois' },
      ],
      lignes: [], total: 0, page: 1, parPage: 25,
      totaux: { encaisse: 0, assurance: 0, montant: 0, parMoyen: {}, parType: {} },
      types: {}, statuts: {}, moyens: [], assurances: [],
      chargement: false, minuterie: null, export_: { enCours: false },
    }
  },
  computed: {
    perm() { return (c) => this.$store.getters.hasPermission(c) },
    peutExporter() { return this.perm('TRANS_LIST_AUTRES_SERVICES_EXPORT') || this.perm('TRANS_LIST_CONSULTATION_EXPORT') },
    peutImprimer() { return this.perm('TRANS_LIST_AUTRES_SERVICES_IMPRESSION') || this.perm('TRANS_LIST_CONSULTATION_IMPRESSION') },
  },
  watch: {
    '$route.query.type'(t) { this.f.type = t || ''; this.relancer() },
  },
  async created() {
    this.f.type = this.$route.query.type || ''
    this.charger()
    try { this.moyens = (await api.liste('moyen_paiements', { itemsperpage: 100 })).data.data || [] } catch (e) { this.moyens = [] }
    if (this.$store.getters.hasModule('ASSURANCES')) {
      try { this.assurances = (await api.liste('assurances', { itemsperpage: 100 })).data.data || [] } catch (e) { this.assurances = [] }
    }
  },
  methods: {
    fcfa(v) { return fcfaImpression(v) },
    params(page = this.page, parPage = this.parPage) {
      return { ...this.f, page, itemsperpage: parPage }
    },
    async charger() {
      this.chargement = true
      try {
        const r = (await api.liste('paiements', this.params())).data
        this.lignes = r.data
        this.total = r.meta.total_records
        this.totaux = r.totaux
        this.types = r.types
        this.statuts = r.statuts
      } catch (err) {
        notifier(messageErreur(err, 'Impossible de charger les encaissements.'))
      } finally {
        this.chargement = false
      }
    },
    relancer() { this.page = 1; this.charger() },
    relancerApres() { clearTimeout(this.minuterie); this.minuterie = setTimeout(() => this.relancer(), 350) },
    choisirPeriode(code) {
      this.periode = code
      this.f.au = aujourdhui()
      this.f.du = code === 'jour' ? aujourdhui() : code === 'semaine' ? ilYa(6) : debutMois()
      this.relancer()
    },
    reinitialiser() {
      this.f = { recherche: '', du: aujourdhui(), au: aujourdhui(), type: '', statut: '', moyen: '', assurance: '' }
      this.periode = 'jour'
      this.relancer()
    },
    peutCorriger(p) {
      if (!p.correction || p.correction === 'VENTE') return false
      if (p.correction === 'REMBOURSEMENT') return this.perm('PAIEMENT_REMBOURSER')
      return this.perm('PAIEMENT_ANNULER') || (p.type === 'CONSULTATION' && this.perm('CONSULT_ANNULER_PAIEMENT'))
    },
    async corriger(p) {
      const remb = p.correction === 'REMBOURSEMENT'
      const r = await demanderMotif({
        titre: remb ? `Rembourser le reçu ${p.numero}` : `Annuler le reçu ${p.numero}`,
        message: remb
          ? `La caisse où ce paiement a été reçu est déjà clôturée. Le patient sera remboursé de ${fcfaImpression(p.partPatient)} depuis votre caisse, et le remboursement sera tracé.`
          : `Le paiement de ${fcfaImpression(p.partPatient)} sera annulé. L'acte redeviendra à payer${p.partAssurance > 0 ? " et la créance d'assurance sera annulée" : ''}.`,
        libelleValider: remb ? 'Rembourser' : "Annuler l'encaissement",
        choix: remb ? { libelle: 'Rendu par', options: this.moyens.map(m => ({ valeur: m.id, texte: m.nom })), valeur: this.moyens.find(m => m.nom === p.moyen)?.id } : null,
      })
      if (!r) return
      try {
        const res = (await api.post(`paiements/${p.id}/annuler`, { motif: r.motif, moyenRemboursement: r.choix || null })).data
        notifier(res.message, 'success')
        this.charger()
      } catch (err) {
        notifier(messageErreur(err, "L'annulation a échoué."))
      }
    },
    async imprimerRecu(p) {
      await imprimer(documentRecu(p, 'ticket'))
    },
    async toutesLesLignes() {
      const toutes = []
      for (let page = 1; page <= 50; page++) {
        const r = (await api.liste('paiements', this.params(page, 100))).data
        toutes.push(...r.data)
        if (page >= r.meta.pages) break
      }
      return toutes
    },
    async exporterCsv() {
      this.export_.enCours = true
      try {
        const lignes = await this.toutesLesLignes()
        const entete = ['Reçu', 'Date', 'Patient', 'Dossier', 'Nature', 'Référence', 'Montant', 'Part assurance', 'Assurance', 'Part patient', 'Moyen', 'Caissier', 'Statut', 'Motif']
        const csv = [entete, ...lignes.map(p => [
          p.numero, dateFr(p.date, true), p.patient?.nom || '', p.patient?.numeroDossier || '', this.types[p.type] || p.type, p.acte?.numero || '',
          p.montant, p.partAssurance, p.assurance || '', p.partPatient, p.moyen || '', p.caissier || '', this.statuts[p.statut] || p.statut, p.motifAnnulation || '',
        ])].map(l => l.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';')).join('\r\n')
        const lien = document.createElement('a')
        lien.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }))
        lien.download = `encaissements-${this.f.du || 'debut'}-${this.f.au || 'fin'}.csv`
        lien.click()
        URL.revokeObjectURL(lien.href)
      } catch (err) {
        notifier(messageErreur(err, "L'export a échoué."))
      } finally {
        this.export_.enCours = false
      }
    },
    async imprimerListe() {
      const lignes = await this.toutesLesLignes()
      const corps = `
        <table class="lignes"><thead><tr><th>Reçu</th><th>Date</th><th>Patient</th><th>Nature</th><th class="droite">Assurance</th><th class="droite">Patient</th><th>Moyen</th><th>Statut</th></tr></thead><tbody>
        ${lignes.map(p => `<tr><td>${e(p.numero)}</td><td>${e(dateFr(p.date, true))}</td><td>${e(p.patient?.nom || '—')}</td><td>${e(this.types[p.type] || p.type)}</td><td class="droite">${fcfaImpression(p.partAssurance)}</td><td class="droite">${fcfaImpression(p.partPatient)}</td><td>${e(p.moyen || '')}</td><td>${e(this.statuts[p.statut] || p.statut)}</td></tr>`).join('')}
        </tbody></table>
        <table class="totaux"><tr><td>Reçu des patients</td><td class="droite">${fcfaImpression(this.totaux.encaisse)}</td></tr><tr><td>Part des assurances</td><td class="droite">${fcfaImpression(this.totaux.assurance)}</td></tr></table>`
      await imprimer({ titre: 'Encaissements', sousTitre: `Du ${dateFr(this.f.du)} au ${dateFr(this.f.au)}`, corps, orientation: 'landscape' })
    },
    dateHeure(d) { return d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '' },
    tronquer(t) { return t && t.length > 40 ? t.slice(0, 40) + '…' : t },
  },
}
</script>

<style scoped>
.header-clinique { padding: 15px 20px; background-color: #e9f5ff; border-radius: 12px; }
.card-clinique { background: #fff; border-radius: 12px; border: 1px solid #eef1f5; box-shadow: 0 1px 2px rgba(15, 23, 42, .04); }
</style>
